import { useState, type FormEvent } from "react";
import {
  useAddChecklistItem,
  useChecklists,
  useClearChecked,
  useCreateChecklist,
  useDeleteChecklist,
  useRemoveChecklistItem,
  useToggleChecklistItem,
} from "./hooks";
import type { Checklist } from "./types";

export function ChecklistsView() {
  const { data, isLoading, error } = useChecklists();
  const create = useCreateChecklist();
  const [name, setName] = useState("");

  function onCreate(event: FormEvent) {
    event.preventDefault();
    const value = name.trim();
    if (!value) return;
    create.mutate(value, { onSuccess: () => setName("") });
  }

  return (
    <div className="view">
      <form className="capture" onSubmit={onCreate}>
        <input
          className="capture__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nowa checklista (np. Zakupy)…"
          aria-label="Nowa checklista"
        />
        <button className="btn btn--primary" type="submit" disabled={!name.trim()}>
          Dodaj
        </button>
      </form>
      {create.error && <p className="error">{(create.error as Error).message}</p>}

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać checklist.</p>}
      {data && data.length === 0 && <p className="muted">Brak checklist.</p>}

      {data?.map((checklist) => (
        <ChecklistCard key={checklist.id} checklist={checklist} />
      ))}
    </div>
  );
}

function ChecklistCard({ checklist }: { checklist: Checklist }) {
  const toggle = useToggleChecklistItem();
  const addItem = useAddChecklistItem();
  const removeItem = useRemoveChecklistItem();
  const clearChecked = useClearChecked();
  const deleteList = useDeleteChecklist();
  const [text, setText] = useState("");

  const checkedCount = checklist.items.filter((i) => i.checked).length;

  function onAdd(event: FormEvent) {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    addItem.mutate({ checklistId: checklist.id, text: value }, { onSuccess: () => setText("") });
  }

  return (
    <section className="card">
      <header className="card__head">
        <h3 className="card__title">
          {checklist.name}
          {checklist.context && <span className="badge">{checklist.context}</span>}
        </h3>
        <button
          className="btn btn--small btn--danger"
          title="Usuń checklistę"
          onClick={() => {
            if (confirm(`Usunąć checklistę „${checklist.name}”?`)) deleteList.mutate(checklist.id);
          }}
        >
          🗑
        </button>
      </header>

      <ul className="rows rows--nested">
        {checklist.items.map((item) => (
          <li key={item.id} className="row">
            <label className="row__check">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() =>
                  toggle.mutate({
                    checklistId: checklist.id,
                    itemId: item.id,
                    checked: !item.checked,
                  })
                }
                aria-label={item.text}
              />
            </label>
            <div className="row__main">
              <span className={`row__title ${item.checked ? "row__title--done" : ""}`}>
                {item.text}
              </span>
            </div>
            <div className="row__actions">
              <button
                className="btn btn--small btn--danger"
                title="Usuń"
                onClick={() => removeItem.mutate({ checklistId: checklist.id, itemId: item.id })}
              >
                ✕
              </button>
            </div>
          </li>
        ))}
      </ul>

      <form className="capture" onSubmit={onAdd}>
        <input
          className="capture__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Dodaj pozycję…"
          aria-label={`Dodaj pozycję do ${checklist.name}`}
        />
        <button className="btn btn--primary" type="submit" disabled={!text.trim()}>
          +
        </button>
      </form>

      {checkedCount > 0 && (
        <button className="btn btn--block" onClick={() => clearChecked.mutate(checklist.id)}>
          Usuń odhaczone ({checkedCount})
        </button>
      )}
    </section>
  );
}
